import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Combobox, useCombobox } from '@any-ui-react/core';

import { AnyChatSaleChannelType, ChatType } from '@anyx/common-graphql';
import { CrmChannelIcon } from '@anyx/feature-crm';
import { DotsHorizontalIcon, EditIcon } from '@anyx/shared-icons';

import { CustomerNoteActivityForm } from '../customer-note-activity-form';
import { useCustomerActivityTimelineCtx } from '../CustomerActivityTimelineContext';

import { CustomerActivityTimelineNode } from './CustomerActivityTimelineNode';

export interface CustomerActivityTimelineBodyProps {
  channel?: ChatType | AnyChatSaleChannelType | 'ANYCREATOR';
  children: React.ReactNode;
  time: string;
}

export const CustomerActivityTimelineBody = ({
  channel,
  children,
  time,
}: CustomerActivityTimelineBodyProps) => {
  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-2">
        {channel && (
          <div className="border-r pr-2">
            <CrmChannelIcon channel={channel} type="fa" className="h-4 w-4" />
          </div>
        )}
        {time && <span className="text-gray-7 text-2xs">{time}</span>}
      </div>
      {children}
    </div>
  );
};

export type CustomerActivityTimelineItemProps = Omit<
  CustomerActivityTimelineBodyProps,
  'children'
> & { content: React.ReactNode };

export const CustomerActivityTimelineItem = ({
  content,
  ...props
}: CustomerActivityTimelineItemProps) => {
  return (
    <CustomerActivityTimelineNode>
      <CustomerActivityTimelineBody {...props}>
        <p className="text-sm wrap-anywhere">{content}</p>
      </CustomerActivityTimelineBody>
    </CustomerActivityTimelineNode>
  );
};

type CustomerActivityTimelineNoteItemProps = Omit<CustomerActivityTimelineBodyProps, 'children'> & {
  id: string;
  note: string;
  editor: string;
  editable?: boolean;
};

export const CustomerActivityTimelineNoteItem = ({
  id,
  note,
  editor,
  editable,
  ...props
}: CustomerActivityTimelineNoteItemProps) => {
  const { t } = useTranslation(['gql', 'shared']);
  const { onUpdateNoteActivity, onDeleteNoteActivity } = useCustomerActivityTimelineCtx();

  const combobox = useCombobox();
  const [isEditing, setIsEditing] = useState(false);

  const handleCancel = () => {
    setIsEditing(false);
  };

  const onEdit = () => {
    combobox.closeDropdown();
    setIsEditing(true);
  };

  const handleUpdateNoteActivity = async (payload: { id: string; note: string }) => {
    const success = await onUpdateNoteActivity?.(payload);

    if (success) {
      setIsEditing(false);
      return true;
    }

    return false;
  };

  const handleDeleteNoteActivity = async () => {
    combobox.closeDropdown();
    const success = await onDeleteNoteActivity?.({ id });

    if (success) {
      setIsEditing(false);
      return true;
    }

    return false;
  };

  return (
    <div className="shadow-card-2 relative z-10 rounded-sm bg-white px-3 pt-2 pb-3">
      <CustomerActivityTimelineBody {...props}>
        {editable && isEditing ? (
          <CustomerNoteActivityForm
            id={id}
            note={note}
            onCancel={handleCancel}
            onUpdateNoteActivity={handleUpdateNoteActivity}
          />
        ) : (
          <p className="mt-1 text-sm wrap-anywhere">{note}</p>
        )}
        {editor && !isEditing && (
          <div className="flex items-center gap-1">
            <EditIcon className="text-gray-7 h-3 w-3" />
            <span className="text-2xs text-gray-7">{editor}</span>
          </div>
        )}
      </CustomerActivityTimelineBody>
      {editable && !isEditing && (
        <div className="absolute top-2 right-4">
          <Combobox store={combobox} position="bottom-end">
            <Combobox.Target>
              <DotsHorizontalIcon
                className="border-gray-5 hover:bg-gray-1 text-gray-7 shrink-0 cursor-pointer self-start rounded border"
                onClick={() => {
                  combobox.toggleDropdown();
                }}
              />
            </Combobox.Target>
            <Combobox.Dropdown classNames={{ dropdown: 'p-2 min-w-[116px]' }}>
              <Combobox.Option value="edit" className="flex h-8 items-center px-2" onClick={onEdit}>
                <span>{t('shared.button.edit', { ns: 'shared' })}</span>
              </Combobox.Option>
              <Combobox.Option
                value="delete"
                className="flex h-8 items-center px-2"
                onClick={handleDeleteNoteActivity}
              >
                <span>
                  {t('shared.button.delete', {
                    ns: 'shared',
                  })}
                </span>
              </Combobox.Option>
            </Combobox.Dropdown>
          </Combobox>
        </div>
      )}
    </div>
  );
};
